class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.closeButton = document.querySelector(".quick-add-modal__close");
    this.currentProductHandle = null;
    this.currentProductData = null;
    this.currentZoomIndex = 0; // Track current zoomed image index

    this.initEvents();
  }

  initEvents() {
    this.closeButton.addEventListener("click", () => this.close());
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    document.querySelectorAll(".quick-add-btn").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const productHandle = button.dataset.productHandle;
        if (productHandle) this.open(productHandle);
      });
    });
  }

  open(productHandle) {
    if (
      this.currentProductHandle === productHandle &&
      this.modal.style.display === "flex"
    ) {
      return;
    }

    this.currentProductHandle = productHandle;
    this.showLoading();
    this.modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    this.fetchProduct(productHandle);
  }

  close() {
    this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.currentProductHandle = null;
    this.currentProductData = null;
  }

  showLoading() {
    this.modalContent.innerHTML = `
      <div class="quick-add-loading">
        <p>Loading product...</p>
      </div>
    `;
  }

  showError() {
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>Failed to load product. Please try again.</p>
        <button onclick="quickAddModal.close()">Close</button>
      </div>
    `;
  }

  async fetchProduct(productHandle) {
    try {
      const response = await fetch(
        `/products/${productHandle}?section_id=product-quick-add`
      );
      if (!response.ok) throw new Error("Failed to fetch product");

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const productHTML = doc.querySelector(".quick-add-product");

      if (!productHTML) throw new Error("Invalid product content");

      this.modalContent.innerHTML = "";
      this.modalContent.appendChild(productHTML);

      // Store product data
      this.currentProductData = JSON.parse(productHTML.dataset.productVariants);

      this.initProductScripts();
    } catch (error) {
      console.error("Quick add error:", error);
      this.showError();
    }
  }

  initProductScripts() {
    const productContainer =
      this.modalContent.querySelector(".quick-add-product");
    if (!productContainer) return;

    const productHandle = productContainer.dataset.productHandle;
    if (!productHandle) return;

    this.initImageSlider(productContainer);
    this.initImageZoom(productContainer); // Add this line
    this.initVariantSelection(productContainer, productHandle);
    this.initQuantitySelector(productContainer);
    this.initFormSubmission(productContainer);
  }

  initImageSlider(container) {
    const slider = container.querySelector(".quick-add-slides");
    const slides = container.querySelectorAll(".quick-add-slide");
    const prevBtn = container.querySelector(".quick-add-prev");
    const nextBtn = container.querySelector(".quick-add-next");
    const counter = container.querySelector(
      ".quick-add-counter .current-slide"
    );

    if (!slider || slides.length === 0) return;

    let currentSlide = 0;
    const slideCount = slides.length;

    const updateCounter = () => {
      if (counter) counter.textContent = currentSlide + 1;
    };

    const goToSlide = (index) => {
      currentSlide = (index + slideCount) % slideCount;
      slider.style.transform = `translateX(-${currentSlide * 100}%)`;
      updateCounter();
    };

    const nextSlide = () => goToSlide(currentSlide + 1);
    const prevSlide = () => goToSlide(currentSlide - 1);

    if (nextBtn) nextBtn.addEventListener("click", nextSlide);
    if (prevBtn) prevBtn.addEventListener("click", prevSlide);

    // Touch events for swipe
    let touchStartX = 0;
    let touchEndX = 0;

    slider.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    slider.addEventListener(
      "touchend",
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        if (touchEndX < touchStartX - 50) nextSlide();
        if (touchEndX > touchStartX + 50) prevSlide();
      },
      { passive: true }
    );

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    });

    updateCounter();
  }

  // Add these new methods for zoom functionality
  initImageZoom(container) {
    const zoomPopup = container.querySelector(".quick-image-zoom-popup");
    const zoomImages = container.querySelectorAll(".quick-zoom-image");
    const zoomImageContainer = container.querySelector(
      ".quick-zoom-image-container"
    );
    const zoomCloseBtn = container.querySelector(".quick-zoom-close-btn");
    const zoomPrevBtn = container.querySelector(".quick-zoom-nav.prev");
    const zoomNextBtn = container.querySelector(".quick-zoom-nav.next");
    const zoomCurrentSlide = container.querySelector(
      ".quick-zoom-counter .quick-current-slide"
    );
    const zoomTotalSlides = container.querySelector(
      ".quick-zoom-counter .quick-total-slides"
    );
    const mainZoomBtn = container.querySelector(".quick-main_zoom");

    if (!zoomPopup) return;

    // Initialize zoom popup
    const initZoomPopup = () => {
      zoomTotalSlides.textContent = zoomImages.length;

      // Open zoom popup
      mainZoomBtn.addEventListener("click", () => {
        const currentSlide = container.querySelector(
          ".quick-add-slide[data-index]"
        );
        this.currentZoomIndex = parseInt(currentSlide.dataset.index || "0");
        updateZoomPopup();
        zoomPopup.classList.add("active");
        document.body.style.overflow = "hidden";
      });

      // Close zoom popup
      zoomCloseBtn.addEventListener("click", () => {
        zoomPopup.classList.remove("active");
        document.body.style.overflow = "hidden"; // Keep hidden because modal is still open
      });

      // Close when clicking outside image
      zoomPopup.addEventListener("click", (e) => {
        if (e.target === zoomPopup) {
          zoomPopup.classList.remove("active");
          document.body.style.overflow = "hidden";
        }
      });

      // Keyboard navigation
      document.addEventListener("keydown", (e) => {
        if (zoomPopup.classList.contains("active")) {
          if (e.key === "Escape") {
            zoomPopup.classList.remove("active");
            document.body.style.overflow = "hidden";
          }
          if (e.key === "ArrowRight") nextZoomImage();
          if (e.key === "ArrowLeft") prevZoomImage();
        }
      });

      // Navigation buttons
      zoomPrevBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        prevZoomImage();
      });

      zoomNextBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        nextZoomImage();
      });

      // Swipe gestures for mobile
      let touchStartX = 0;
      let touchEndX = 0;

      zoomImageContainer.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      zoomImageContainer.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          handleZoomSwipe();
        },
        { passive: true }
      );

      const handleZoomSwipe = () => {
        if (touchEndX < touchStartX - 50) nextZoomImage();
        if (touchEndX > touchStartX + 50) prevZoomImage();
      };

      const nextZoomImage = () => {
        this.currentZoomIndex = (this.currentZoomIndex + 1) % zoomImages.length;
        updateZoomPopup();
      };

      const prevZoomImage = () => {
        this.currentZoomIndex =
          (this.currentZoomIndex - 1 + zoomImages.length) % zoomImages.length;
        updateZoomPopup();
      };

      const updateZoomPopup = () => {
        zoomImages.forEach((img, index) => {
          img.classList.toggle("active", index === this.currentZoomIndex);
        });
        if (zoomCurrentSlide) {
          zoomCurrentSlide.textContent = this.currentZoomIndex + 1;
        }
      };
    };

    initZoomPopup.call(this); // Call with this context
  }

  initVariantSelection(container, productHandle) {
    const variantRadios = container.querySelectorAll(
      '.quick-add-swatch input[type="radio"]'
    );
    const addToCartBtn = container.querySelector(".quick-add-atc");
    const priceDisplay = container.querySelector(".quick-add-current-price");
    const comparePriceDisplay = container.querySelector(
      ".quick-add-compare-price"
    );
    const savingsDisplay = container.querySelector(".quick-add-savings");

    if (variantRadios.length === 0) return;

    variantRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selectedOptions = [];

        container
          .querySelectorAll(".quick-add-option")
          .forEach((optionGroup) => {
            const selectedOption = optionGroup.querySelector(
              'input[type="radio"]:checked'
            );
            if (selectedOption) selectedOptions.push(selectedOption.value);
          });

        const selectedVariant = this.currentProductData.find((variant) => {
          return variant.options.every(
            (option, index) => option === selectedOptions[index]
          );
        });

        if (selectedVariant) {
          const variantInput = container.querySelector('input[name="id"]');
          if (variantInput) variantInput.value = selectedVariant.id;

          // Update price display
          if (priceDisplay) {
            priceDisplay.textContent = Shopify.formatMoney(
              selectedVariant.price,
              window.money_format
            );
          }

          if (comparePriceDisplay) {
            if (selectedVariant.compare_at_price > selectedVariant.price) {
              comparePriceDisplay.textContent = Shopify.formatMoney(
                selectedVariant.compare_at_price,
                window.money_format
              );
              comparePriceDisplay.style.display = "inline";
              if (savingsDisplay) {
                savingsDisplay.textContent = `Save ${
                  (Shopify.formatMoney(
                    selectedVariant.compare_at_price - selectedVariant.price
                  ),
                  window.money_format)
                }`;
                savingsDisplay.style.display = "inline";
              }
            } else {
              comparePriceDisplay.style.display = "none";
              if (savingsDisplay) savingsDisplay.style.display = "none";
            }
          }

          // Update button
          if (addToCartBtn) {
            addToCartBtn.disabled = !selectedVariant.available;
            addToCartBtn.textContent = selectedVariant.available
              ? `Add to Cart`
              : "Sold Out";
          }

          if (selectedVariant.featured_image) {
            const newImageSrc =
              selectedVariant.featured_image.src.split("?")[0] + "?width=800";
            const mainImage = document.querySelector(".quick-main-image");

            mainImage.src = newImageSrc;
            mainImage.alt = selectedVariant.featured_image.alt;
            mainImage.dataset.variantImage = selectedVariant.id;
          }
        }
      });
    });
  }

  initQuantitySelector(container) {
    const minusBtn = container.querySelector(".quantity-btn.minus");
    const plusBtn = container.querySelector(".quantity-btn.plus");
    const quantityInput = container.querySelector(".quantity-input");
    const stockError = container.querySelector(".stock-error");
    const productContainer = container.closest(".quick-add-product");

    if (!minusBtn || !plusBtn || !quantityInput) return;

    const getMaxQuantity = () => {
      const inventoryPolicy = productContainer.dataset.inventoryPolicy;
      const inventoryManagement = productContainer.dataset.inventoryManagement;

      if (inventoryManagement === "shopify" && inventoryPolicy === "deny") {
        return parseInt(productContainer.dataset.maxQuantity) || 9999;
      }
      return 9999; // No limit if not tracking inventory
    };

    minusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      if (currentValue > 1) {
        quantityInput.value = currentValue - 1;
        stockError.textContent = ""; // Clear error when decreasing
      }
    });

    plusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const maxQuantity = getMaxQuantity();

      if (currentValue >= maxQuantity) {
        stockError.textContent = `Only ${maxQuantity} available in stock`;
        return;
      }

      quantityInput.value = currentValue + 1;
      stockError.textContent = ""; // Clear error when increasing within limits
    });

    quantityInput.addEventListener("change", () => {
      const maxQuantity = getMaxQuantity();
      let newValue = parseInt(quantityInput.value) || 1;

      if (newValue < 1) newValue = 1;
      if (newValue > maxQuantity) {
        newValue = maxQuantity;
        stockError.textContent = `Only ${maxQuantity} available in stock`;
      } else {
        stockError.textContent = "";
      }

      quantityInput.value = newValue;
    });
  }

  initFormSubmission(container) {
    const form = container.querySelector(".quick-add-form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector(".quick-add-atc");
      const originalText = submitBtn.textContent;
      const quantityInput = form.querySelector(".quantity-input");
      const stockError = form.querySelector(".stock-error");
      const productContainer = container.closest(".quick-add-product");

      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
      const maxQuantity =
        parseInt(productContainer.dataset.maxQuantity) || 9999;
      const inventoryPolicy = productContainer.dataset.inventoryPolicy;
      const inventoryManagement = productContainer.dataset.inventoryManagement;

      // Validate stock
      if (
        inventoryManagement === "shopify" &&
        inventoryPolicy === "deny" &&
        quantity > maxQuantity
      ) {
        stockError.textContent = `Only ${maxQuantity} available in stock`;
        quantityInput.value = maxQuantity;
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = "Adding...";

      try {
        const formData = new FormData(form);

        const cartUrl = window.routes.cart_add_url + ".js";

        const response = await fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [
              {
                id: formData.get("id"),
                quantity: Math.min(quantity, maxQuantity),
              },
            ],
          }),
        });

        const data = await response.json();

        if (response.ok) {
          submitBtn.textContent = "Added!";

          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            action: "add",
            variant: data,
            formData: Object.fromEntries(formData),
            product: this.currentProductData,
          });

          // Close modal after delay
          setTimeout(() => {
            this.close();
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1000);
        } else {
          throw new Error(data.description || "Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);

        // Publish error event
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "quick-add-modal",
          productHandle: this.currentProductHandle,
        });

        submitBtn.textContent = "Error - Try Again";
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    });
  }
}

// Initialize everything when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize global instances
  window.quickAddModal = new QuickAddModal();

  // Event delegation for color swatches
  document.addEventListener("click", function (e) {
    // Color swatches
    const colorOption = e.target.closest(
      ".card-product__color-option:not(.disabled)"
    );
    if (colorOption) {
      e.preventDefault();
      if (colorOption.getAttribute("aria-current") === "true") return;

      const productCard = colorOption.closest(".card-product");
      const desktopImage = productCard?.querySelector(
        ".card-product__image-link.hidden-mobile .card-product__image"
      );
      const mobileImage = productCard?.querySelector(
        ".card-product__image-container.visible-mobile .card-product__image"
      );

      // Update active state
      colorOption
        .closest(".card-product__colors")
        .querySelectorAll(".card-product__color-option")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      colorOption.setAttribute("aria-current", "true");

      // Update images
      const newImageSrc = colorOption.dataset.imageSrc;
      const newMobileImageSrc = colorOption.dataset.imageSrcMobile;
      const newAltText = colorOption.dataset.imageAlt;

      if (desktopImage && newImageSrc) desktopImage.src = newImageSrc;
      if (mobileImage && newMobileImageSrc) mobileImage.src = newMobileImageSrc;
      if (desktopImage) desktopImage.alt = newAltText;
      if (mobileImage) mobileImage.alt = newAltText;
    }

    // Quick add buttons (works for both static and dynamic cards)
    const quickAddBtn = e.target.closest(".quick-add-btn");
    if (quickAddBtn && window.quickAddModal) {
      e.preventDefault();
      const productHandle = quickAddBtn.dataset.productHandle;
      window.quickAddModal.open(productHandle);
    }
  });
});
